/** Adequacy-questionnaire hooks (#52). */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { aqApi, suitabilityConversionApi } from '../api/aq';
import { ApiError } from '../api/client';
import type {
  AqPreview, AqResponse, AqSubmission, Page, SuitabilityConversionRow, UUID,
} from '../api/types';

export const aqKeys = {
  list: (agreementId: UUID) => ['aq-responses', agreementId] as const,
};

export function useAqResponses(agreementId: UUID | undefined) {
  return useQuery<Page<AqResponse>, ApiError>({
    queryKey: aqKeys.list(agreementId ?? '' as UUID),
    queryFn: () => aqApi.list(agreementId!),
    enabled: !!agreementId,
  });
}

/** Live preview — a mutation (POST) but read-only server-side. */
export function useAqPreview(agreementId: UUID | undefined) {
  return useMutation<AqPreview, ApiError, AqSubmission>({
    mutationFn: (body) => aqApi.preview(agreementId!, body),
  });
}

export function useCreateAqResponse(agreementId: UUID | undefined) {
  const qc = useQueryClient();
  return useMutation<AqResponse, ApiError, AqSubmission>({
    mutationFn: (body) => aqApi.create(agreementId!, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['aq-responses'] });
      qc.invalidateQueries({ queryKey: ['change-requests'] });
    },
  });
}

export function useSuitabilityConversion() {
  return useQuery<Page<SuitabilityConversionRow>, ApiError>({
    queryKey: ['suitability-conversion'],
    queryFn: suitabilityConversionApi.list,
  });
}
